import { useContext } from "react";
import { GableSide } from "../../models/GableSide";
import { IInteriorProduct } from "../../models/IChainInfo";
import { IObj2d } from "../../models/IObj";
import { hasDimensions, hasProductLine } from "../../services/CabinetValidator";
import { IInteriorItem, InteriorProductType } from "../../services/WebApiClient";
import { GableImage } from "../DoorEditor/GableImage";
import { HorizontalRuler } from "../DoorEditor/HorizontalRuler";
import { VerticalRuler } from "../DoorEditor/VerticalRuler";
import { CommonEditorProps, EditorContext } from "../EditorCommon/EditorContext";
import { InteriorDragContext } from "./InteriorImageDragContext";
import { InteriorImageItem } from "./InteriorImageItem";
import { InteriorImageTop } from "./InteriorImageTop";
import { ItemWarning } from "./ItemWarning";


//Lower numbers are drawn first, higher numbers are drawn on top
function getSortOrder(product: IInteriorProduct): number {
    switch (product.productType) {
        case InteriorProductType.Gable:
        case InteriorProductType.Module:
            return 0;
        default:
            return 1;
    }
}

export function InteriorImageFront(props: { displayHelpTextOnEmpty: boolean }) {
    const ec = useContext(EditorContext);

    if (!hasDimensions(ec.cabinet))
        throw new Error("dimensions not defined");
    if (!hasProductLine(ec.cabinet))
        throw new Error("product line not defined");

    const dragContext = useContext(InteriorDragContext);

    const wallThicknesses = InteriorImageTop.getWallThicknesses(ec);

    let items = ec.cabinet.interiorItems.map(ii => ({
        item: ii,
        product: ec.cabinetService.interior.getProduct(ii.productId)
    }))
    
    items.sort((a, b) => getSortOrder(a.product) - getSortOrder(b.product));

    function shouldShowItem(item: IInteriorItem): boolean {
        return !dragContext.info?.isDragging
            || item !== dragContext.info?.item;
    }
    

    const itemImages = items
        .map(item => <InteriorImageItem key={item.item.index}
            item={item.item}
            product={item.product}
        />);

    const useHelpText = props.displayHelpTextOnEmpty && itemImages.length === 0;

    const helpText = useHelpText
        ? (<text x="160" y="160" style={{ font: "bold 60px sans-serif", color: "grey" }}>
            {ec.translator("interior empty help text", "← ← Træk varer ind fra venstre for at placere dem i skabet. Start med en gavl eller et modul.")}
        </text>)
        : undefined;

    const pulloutViolations = ec.cabinetService.interior
        .getPulloutViolations()
        .filter(shouldShowItem)
        .map(item => <ItemWarning item={item} key={item.index} />)
        ;

    const wallMountViolations = ec.cabinetService.interior
        .getWallMountViolations()
        .filter(shouldShowItem)
        .map(item => <ItemWarning item={item} key={item.index} />)
        ;

    const exceededBounds = ec.cabinetService.interior
        .getExceededBounds()
        .filter(shouldShowItem)
        .map(item => <ItemWarning item={item} key={item.index} />)
        ;

    return (

        <g className="interior-image-front">
            <HorizontalRuler
                x={0}
                length={ec.cabinet.width ?? 0}
                svgY={-InteriorImageTop.wallThickness - HorizontalRuler.height / 2}
            />

            <VerticalRuler
                x={-VerticalRuler.width / 2 - wallThicknesses.left}
                y={0}
                length={ec.cabinet.height}
            />


            <g className="walls">
                {wallThicknesses.left && (
                    <rect x={-wallThicknesses.left}
                        y={0}
                        width={wallThicknesses.left}
                        height={ec.cabinet.height}
                        {...InteriorImageTop.wallDisplayProps}
                    />
                )}
                {wallThicknesses.right && (
                    <rect x={ec.cabinet.width}
                        y={0}
                        width={wallThicknesses.right}
                        height={ec.cabinet.height}
                        {...InteriorImageTop.wallDisplayProps}
                    />
                )}
                <rect x={-wallThicknesses.left}
                    y={-InteriorImageTop.wallThickness}
                    width={ec.cabinet.width + wallThicknesses.left + wallThicknesses.right}
                    height={InteriorImageTop.wallThickness}
                    {...InteriorImageTop.wallDisplayProps}
                />
            </g>


            {itemImages}
            {helpText}
            {pulloutViolations}
            {wallMountViolations}
            {exceededBounds}
            <GableImage side={GableSide.Left} />
            <GableImage side={GableSide.Right} />
        </g>
    );
}


export module InteriorImageFront {

    export function boundingBox(ec: CommonEditorProps): IObj2d {

        const wallthicknesses = InteriorImageTop.getWallThicknesses(ec);

        return {
            position: {
                x: -wallthicknesses.left - VerticalRuler.width,
                y: -InteriorImageTop.wallThickness - HorizontalRuler.height
            },
            size: {
                x: (ec.cabinet.width ?? 0)
                    + wallthicknesses.left
                    + wallthicknesses.right
                    + VerticalRuler.width,
                y: (ec.cabinet.height ?? 0)
                    + HorizontalRuler.height
                    + InteriorImageTop.wallThickness
            },
        }
    }
}
